/*
 * WebYes RUM SDK — rum-sdk.ts
 * -------------------------------------------------------------
 * Lightweight, privacy-safe Real User Monitoring SDK.
 *
 * Build target: single self-executing UMD bundle (rum-sdk.min.js)
 * Bundler suggestion: tsup (esbuild) — tiny, fast, no config needed.
 *   npx tsup rum-sdk.ts --dts false --format iife --minify --sourcemap --global-name=WebYesRUM --out-dir dist
 * CDN path should serve: dist/rum-sdk.min.js
 *
 * Notes / Limits (browser APIs):
 *  - HTTP status code and protocol (e.g., h2) of the main document are not reliably exposed to JS.
 *    Leave as null and let the backend enrich from the request.
 *  - Region should be resolved server-side from IP (left empty here).
 *  - No PII is collected; element attribution is reduced to a safe CSS-like selector (no innerText/HTML).
 */

// Minimal type shims for the web-vitals package (installed and bundled at build time)
// We import attribution-enabled functions so we can safely extract element selectors.
import { onCLS, onFID, onINP, onLCP, onFCP, onTTFB, type MetricWithAttribution } from 'web-vitals/attribution';

/** Utility: generate RFC4122 v4-like ID (not cryptographically strong) */
function uuidv4(): string {
  // Use crypto if available for better randomness
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as any).randomUUID();
  }
  const rnd = (n = 16) => Array.from({ length: n }, () => Math.floor(Math.random() * 256));
  const b = rnd(16);
  // Set version and variant
  b[6] = (b[6] & 0x0f) | 0x40; // v4
  b[8] = (b[8] & 0x3f) | 0x80; // variant
  const hex = (x: number) => x.toString(16).padStart(2, '0');
  return (
    hex(b[0]) + hex(b[1]) + hex(b[2]) + hex(b[3]) + '-' +
    hex(b[4]) + hex(b[5]) + '-' +
    hex(b[6]) + hex(b[7]) + '-' +
    hex(b[8]) + hex(b[9]) + '-' +
    hex(b[10]) + hex(b[11]) + hex(b[12]) + hex(b[13]) + hex(b[14]) + hex(b[15])
  );
}

/** Safe selector from an element for attribution without text */
function safeSelector(el: Element | undefined | null): string | null {
  if (!el || !(el instanceof Element)) return null;
  if (el.id) return `#${el.id}`;
  let name = el.tagName.toLowerCase();
  if (el.classList && el.classList.length) {
    const classes = Array.from(el.classList).slice(0, 3).map(c => `.${c}`).join('');
    name += classes;
  }
  // Clamp depth to avoid leaking structure
  let depth = 0;
  let cur: Element | null = el.parentElement;
  while (cur && depth < 2) {
    let part = cur.tagName.toLowerCase();
    if (cur.id) part = `#${cur.id}`;
    else if (cur.classList && cur.classList.length) part += '.' + Array.from(cur.classList).slice(0,2).join('.');
    name = part + '>' + name;
    cur = cur.parentElement; depth++;
  }
  return name;
}

/** Safe text extraction from an element (opt-in only) */
function safeText(el: Element | null | undefined, max = 120): string | null {
  if (!el) return null;
  try {
    const t = (el.textContent || '').replace(/\s+/g, ' ').trim();
    if (!t) return null;
    return t.slice(0, max);
  } catch { return null; }
}

/** Device classification */
function getDeviceType(): 'desktop'|'mobile'|'tablet' {
  const ua = navigator.userAgent || '';
  const isIpad = /iPad/.test(ua) || (navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1);
  const isTablet = /Tablet|PlayBook/.test(ua) || isIpad || (/Android/.test(ua) && !/Mobile/.test(ua));
  const isMobile = /Mobi|iPhone|Android/.test(ua) && !isTablet;
  return isTablet ? 'tablet' : isMobile ? 'mobile' : 'desktop';
}

/** Network type via Network Information API (not supported everywhere) */
function getNetworkType(): string {
  const nav = navigator as any;
  const conn = nav.connection || nav.mozConnection || nav.webkitConnection;
  return conn?.effectiveType || '';
}

/** Navigation type */
function getNavigationType(): string {
  const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  return nav?.type || 'navigate';
}

/** Safari detection for CORS workarounds */
function isSafari(): boolean {
  const ua = navigator.userAgent || '';
  return /^((?!chrome|android).)*safari/i.test(ua) || 
         (/iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream);
}


/** Browser brand from User-Agent Client Hints (Chromium only) */
function getBrandFromUAData(): string | null {
  const nav = navigator as any;
  const uaData = nav.userAgentData;
  if (!uaData || !Array.isArray(uaData.brands)) return null;

  const brands: string[] = uaData.brands
    .map((b: any) => b.brand || b.brandName || '')
    .filter(Boolean);

  if (!brands.length) return null;

  // Ignore noisy "Not(A:Brand" entries
  const filtered = brands.filter(b => !/Not.A.Brand/i.test(b));
  const list = filtered.length ? filtered : brands;

  const priority = [
    'Brave',
    'Opera',
    'Microsoft Edge',
    'Edge',
    'Google Chrome',
    'Chrome',
    'Chromium',
  ];

  for (const p of priority) {
    const match = list.find(b => b.toLowerCase().includes(p.toLowerCase()));
    if (match) return match;
  }

  // Fallback: first brand
  return list[0] || null;
}

/** Browser detection */
/** Browser detection */
function getBrowserInfo(): { name: string; version: string } {
  const ua = navigator.userAgent || '';
  const navAny = navigator as any;

  let name: string = 'Unknown';
  let version: string = 'Unknown';

  const brand = getBrandFromUAData();

  // 1) Brave (flag on navigator) – must check before generic Chrome
  if (typeof navAny.brave !== 'undefined') {
    name = 'Brave';
  }
  // 2) Prefer userAgentData.brands on Chromium-based browsers
  else if (brand) {
    const b = brand.toLowerCase();

    if (b.includes('brave')) {
      name = 'Brave';
    } else if (b.includes('opera')) {
      name = 'Opera';
    } else if (b.includes('edge') || b.includes('microsoft edge')) {
      name = 'Edge';
    } else if (b.includes('chrome')) {
      name = 'Chrome';
    } else if (b.includes('chromium')) {
      name = 'Chromium';
    }
  }

  // 3) If still Unknown, fall back to existing UA-based detection
  if (name === 'Unknown') {
    // Opera
    if (ua.includes('OPR') || ua.includes('Opera')) {
      name = 'Opera';
      const match = ua.match(/(?:OPR|Opera)\/(\d+\.\d+)/);
      version = match ? match[1] : 'Unknown';
    }
    // Chrome / CriOS (but not Edge)
    else if (ua.includes('CriOS') || (ua.includes('Chrome') && !ua.includes('Edg'))) {
      name = 'Chrome';
      const match = ua.includes('CriOS')
        ? ua.match(/CriOS\/(\d+\.\d+)/)
        : ua.match(/Chrome\/(\d+\.\d+)/);
      version = match ? match[1] : 'Unknown';
    }
    // Edge (desktop and mobile)
    else if (ua.includes('EdgiOS') || ua.includes('Edg')) {
      name = 'Edge';
      const match = ua.includes('EdgiOS')
        ? ua.match(/EdgiOS\/(\d+\.\d+)/)
        : ua.match(/Edg\/(\d+\.\d+)/);
      version = match ? match[1] : 'Unknown';
    }
    // Firefox (desktop and mobile)
    else if (ua.includes('FxiOS') || ua.includes('Firefox')) {
      name = 'Firefox';
      const match = ua.includes('FxiOS')
        ? ua.match(/FxiOS\/(\d+\.\d+)/)
        : ua.match(/Firefox\/(\d+\.\d+)/);
      version = match ? match[1] : 'Unknown';
    }
    // Safari (must check after all other browsers that might include Safari)
    else if (
      ua.includes('Safari') &&
      !ua.includes('Chrome') &&
      !ua.includes('CriOS') &&
      !ua.includes('FxiOS') &&
      !ua.includes('EdgiOS')
    ) {
      name = 'Safari';
      const match = ua.match(/Version\/(\d+\.\d+)/);
      version = match ? match[1] : 'Unknown';
    }
  }

  // 4) If we know the name (from brand or Brave flag) but still no version, derive from UA
  if (version === 'Unknown') {
    if (name === 'Brave' || name === 'Chrome' || name === 'Chromium') {
      const match = ua.match(/Chrome\/(\d+\.\d+)/);
      if (match) version = match[1];
    } else if (name === 'Opera') {
      const match = ua.match(/(?:OPR|Opera)\/(\d+\.\d+)/);
      if (match) version = match[1];
    } else if (name === 'Edge') {
      const match = ua.match(/Edg(?:iOS)?\/(\d+\.\d+)/);
      if (match) version = match[1];
    } else if (name === 'Firefox') {
      const match = ua.match(/Firefox\/(\d+\.\d+)/);
      if (match) version = match[1];
    } else if (name === 'Safari') {
      const match = ua.match(/Version\/(\d+\.\d+)/);
      if (match) version = match[1];
    }
  }

  return { name, version };
}


/** Page metadata */
function collectPageMetadata() {
  const { width, height } = ((): {width:number;height:number} => {
    try { return { width: window.innerWidth, height: window.innerHeight }; } catch { return { width: 0, height: 0 }; }
  })();
  return {
    page_url: location.pathname,
    query_string: location.search.slice(1),
    page_title: document.title || '',
    referral: document.referrer || '',
    width, height,
    dpr: typeof devicePixelRatio === 'number' ? devicePixelRatio : 1,
    status_code: null as number | null, // not reliably exposed to JS
    protocol: null as string | null,    // not reliably exposed to JS
    navigation_type: getNavigationType(),
    network_type: getNetworkType(),
  };
}

/** Storage helpers */
const LS_KEYS = {
  SESSION_ID: 'wy_rum_session_id',
  LAST_SENT_AT: 'wy_rum_last_sent_at',
  LAST_PAGE: 'wy_rum_last_page',
  QUEUE: 'wy_rum_queue',
};

function readLS<T = string>(k: string): T | null {
  try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : null; } catch { return null; }
}
function writeLS(k: string, v: any) {
  try { localStorage.setItem(k, JSON.stringify(v)); } catch {}
}

/** Transport: sendBeacon with fetch fallback */
async function sendPayload(endpoint: string, body: unknown, debug = false,transport: 'beacon' | 'fetch' = 'fetch'): Promise<boolean> {
  const json = JSON.stringify(body);

  // Prefer sendBeacon when explicitly requested (e.g., pagehide)
  if (transport === 'beacon' && typeof navigator !== 'undefined' && 'sendBeacon' in navigator) {
    try {
      const blob = new Blob([json], { type: 'text/plain;charset=UTF-8' });
      const ok = navigator.sendBeacon(endpoint, blob);

      if (debug) {
        console.log('[WebYesRUM] sendBeacon', { endpoint, ok });
      }

      // sendBeacon is fire-and-forget – if it returns true, treat as sent
      return ok;
    } catch (e) {
      if (debug) {
        console.warn('[WebYesRUM] sendBeacon error, falling back to fetch', e);
      }
      // fall through to fetch below
    }
  }
  //Regular fetch path(initial load, retries, queue flush, etc.)
  try {
    // Fetch with explicit CORS mode for Safari compatibility
    const res = await fetch(endpoint, {
      method: 'POST',
      mode: 'cors', // Explicitly set CORS mode - REQUIRED for Safari
      credentials: 'omit', // Don't send cookies (avoids Safari ITP issues)
      body: json,
      headers: {
        'Content-Type': 'application/json',
      },
      keepalive: !isSafari(),  // helps for some unload scenarios in non-Safari
    });
    
    if (debug) {
      const headers: Record<string, string> = {};
      res.headers.forEach((value, key) => {
        headers[key] = value;
      });
      console.log('[WebYesRUM] fetch response', {
        status: res.status,
        statusText: res.statusText,
        headers,
        endpoint,
      });
    }
    
    return res.ok;

  } catch (e) {
    if (debug) {
      console.warn('[WebYesRUM] transport error', {
        error: e,
        endpoint,
        message: e instanceof Error ? e.message : String(e),
      });
    }
    return false;
  }
}

/** Queue for offline / failures */
function enqueue(body: unknown) {
  const q = (readLS<any[]>(LS_KEYS.QUEUE) || []).slice(-50); // cap
  q.push(body);
  writeLS(LS_KEYS.QUEUE, q);
}
async function flushQueue(endpoint: string, debug = false,transport: 'fetch' | 'beacon' = 'fetch') {
  const q = readLS<any[]>(LS_KEYS.QUEUE) || [];
  if (!q.length) return;
  const remaining: any[] = [];
  for (const item of q) {
    const ok = await sendPayload(endpoint, item, debug,transport);
    if (!ok) remaining.push(item);  
  }
  writeLS(LS_KEYS.QUEUE, remaining);
}

/** Core collector */
(function bootstrap() {
  // Find our <script> tag (supports async/defer usage)
  const currentScript = (document.currentScript as HTMLScriptElement | null) || ((): HTMLScriptElement | null => {
    const scripts = document.getElementsByTagName('script');
    for (let i = scripts.length - 1; i >= 0; i--) {
      const s = scripts[i];
      if (s.hasAttribute('data-rum-site-id')) return s;
    }
    return null;
  })();

  if (!currentScript) return;

  // Read attributes
  const SITE_ID = currentScript.getAttribute('data-rum-site-id') || '';
  const DEFAULT_ENDPOINT = 'https://kmhlub884g.execute-api.eu-west-1.amazonaws.com/dev/api/v1/rum';
  const ENDPOINT = currentScript.getAttribute('data-rum-endpoint') || DEFAULT_ENDPOINT;
  const DEBUG = (currentScript.getAttribute('data-rum-debug') || '').toLowerCase() === 'true';
  const SAMPLE = Math.max(0, Math.min(1, parseFloat(currentScript.getAttribute('data-rum-sample') || '1')));
  const ALLOW_TEXT = (currentScript.getAttribute('data-rum-allow-text') || '').toLowerCase() === 'true';

  if (!SITE_ID) {
    if (DEBUG) console.warn('[WebYesRUM] Missing data-rum-site-id. Aborting.');
    return;
  }

  // Sampling (default 100%)
  if (SAMPLE < 1) {
    if (Math.random() > SAMPLE) {
      if (DEBUG) console.log('[WebYesRUM] Sampled out');
      return;
    }
  }

  const website_id = SITE_ID;
  const website_url = location.host;
  const device = getDeviceType();
  const network = getNetworkType();
  const browser = getBrowserInfo();

  // Session management: sticky until 30 minutes idle; lastSentAt tracks page loads
  let session_id = readLS<string>(LS_KEYS.SESSION_ID) || uuidv4();
  writeLS(LS_KEYS.SESSION_ID, session_id);

  let last_sent_at = (readLS<number>(LS_KEYS.LAST_SENT_AT) || 0);
  let last_page = readLS<string>(LS_KEYS.LAST_PAGE) || '';
  writeLS(LS_KEYS.LAST_PAGE, location.pathname + location.search);

  let created_at = Date.now();


  // Flush any queued events first
  if (ENDPOINT) flushQueue(ENDPOINT, DEBUG, 'fetch');

  // Collect metrics using web-vitals (attribution-enabled)
  type Vitals = {
    // Core
    lcp?: number; lcp_ttfb?: number; lcp_load_delay?: number; lcp_render_delay?: number;
    lcp_load_duration?: number; // (aka resourceLoadDuration)
    lcp_element?: string | null; lcp_url?: string | null; lcp_text?: string | null;

    cls?: number; cls_largest_shift_target?: string | null; cls_largest_shift_value?: number | null;

    ttfb?: number;
    ttfb_wait_duration?: number | null;     // waitingDuration
    ttfb_cache_duration?: number | null;    // cacheDuration
    ttfb_dns_duration?: number | null;      // dnsDuration
    ttfb_tcp_duration?: number | null;      // connectionDuration
    ttfb_req_duration?: number | null;      // requestDuration

    fcp?: number;

    inp?: number | null;
    inp_interaction_target?: string | null;
    inp_interaction_type?: string | null;
    inp_input_delay?: number | null;
    inp_processing_duration?: number | null;
    inp_presentation_delay?: number | null;
  };
  // const vitals: Vitals = { inp: null, lcp_text: null };
  function createEmptyVitals(): Vitals {
    return {
      // Core
      lcp: undefined,
      lcp_ttfb: undefined,
      lcp_load_delay: undefined,
      lcp_render_delay: undefined,
      lcp_load_duration: undefined,
      lcp_element: null,
      lcp_url: null,
      lcp_text: null,

      cls: undefined,
      cls_largest_shift_target: null,
      cls_largest_shift_value: null,

      ttfb: undefined,
      ttfb_wait_duration: null,
      ttfb_cache_duration: null,
      ttfb_dns_duration: null,
      ttfb_tcp_duration: null,
      ttfb_req_duration: null,

      fcp: undefined,

      inp: null,
      inp_interaction_target: null,
      inp_interaction_type: null,
      inp_input_delay: null,
      inp_processing_duration: null,
      inp_presentation_delay: null,
    };
  }

  let vitals: Vitals = createEmptyVitals();


  const markSent = (t: number) => { last_sent_at = t; writeLS(LS_KEYS.LAST_SENT_AT, t); };

  // Track current URL for SPA-style navigation
  let currentUrl = location.pathname + location.search + location.hash;

  function stripHash(url: string): string {
    const idx = url.indexOf('#');
    return idx === -1 ? url : url.slice(0, idx);
  }

  function startNewVirtualPageView() {
    // we're now on a new "route" in the same SPA-like page
    created_at = Date.now();
    durationStart = performance.now();
    vitals = createEmptyVitals();

    last_page = location.pathname + location.search;
    writeLS(LS_KEYS.LAST_PAGE, last_page);
  }


  // const durationStart = performance.now();
  let durationStart = performance.now();


  // Helper to prepare and send only on page reload/navigation
   // Helper to prepare and send payloads
   async function maybeSend(reason: 'first' | 'timer' | 'pagehide' | 'visibility') {
    const now = Date.now();

    if (DEBUG) {
      console.log('[WebYesRUM] maybeSend called', {
        reason,
        last_sent_at,
        endpoint: ENDPOINT,
        online: navigator.onLine,
      });
    }

    // For now: always send on page load + unload-style events
    const allow =
      reason === 'first' ||
      reason === 'pagehide' ||
      reason === 'visibility';

    if (!allow) return;


    const pageMeta = collectPageMetadata();
    const payload = {
      website_id,
      unique_session_id: session_id,
      website_url,
      device,
      browser,
      network,
      region: '',
      last_page,
      website_page_metadata: pageMeta,
      vitals,
      activation_time: 0,
      page_restore_time: 0,
      duration: Math.max(0, (performance.now() - durationStart) / 1000),
      doc_prerendered: null,
      initiator: '',
      timestamp: { created_at, sent_at: now },
    };

    if (DEBUG) console.log('[WebYesRUM] payload', payload);

    if (!navigator.onLine) { 
      enqueue(payload); 
      return; 
    }

    if (!ENDPOINT) {
      return; // Can't send without endpoint
    }

    const transport: 'fetch' | 'beacon' = reason === 'pagehide' ? 'beacon' : 'fetch';
    const ok = await sendPayload(ENDPOINT, payload, DEBUG,transport);
    if (!ok) enqueue(payload);
    markSent(now);
  }

    // Detect SPA / AJAX navigations (history API)
    function handleRouteChange() {
      const newUrl = location.pathname + location.search + location.hash;
  
      // Ignore pure hash changes (scrolling to #section, TOC, etc.)
      if (stripHash(newUrl) === stripHash(currentUrl)) {
        currentUrl = newUrl;
        return;
      }
  
      // 1. Send payload for the previous "page"
      void maybeSend('pagehide');
  
      // 2. Update current URL + reset vitals for the new "page"
      currentUrl = newUrl;
      startNewVirtualPageView();
    }
  
    // Patch history.pushState + history.replaceState
    try {
      const originalPushState = history.pushState;
      (history as any).pushState = function (...args: any[]) {
        originalPushState.apply(this, args);
        handleRouteChange();
      };
  
      const originalReplaceState = history.replaceState;
      (history as any).replaceState = function (...args: any[]) {
        originalReplaceState.apply(this, args);
        handleRouteChange();
      };
  
      // Back/forward navigation
      window.addEventListener('popstate', () => {
        handleRouteChange();
      });
    } catch (e) {
      if (DEBUG) {
        console.warn('[WebYesRUM] Failed to attach SPA navigation listeners', e);
      }
    }
  

  // Start listeners
  onLCP((m: MetricWithAttribution) => {
    vitals.lcp = Math.round(m.value);

    const a: any = m.attribution;
    // element + optional text
    const el = a?.largestContentfulPaintElement ?? null;
    vitals.lcp_element = safeSelector(el);
    vitals.lcp_text = ALLOW_TEXT ? safeText(el) : null;

    // timing breakdowns
    if (typeof a?.lcpLoadDelay === 'number')      vitals.lcp_load_delay = Math.round(a.lcpLoadDelay);
    if (typeof a?.lcpRenderDelay === 'number')    vitals.lcp_render_delay = Math.round(a.lcpRenderDelay);
    if (typeof a?.resourceLoadDuration === 'number') vitals.lcp_load_duration = Math.round(a.resourceLoadDuration);

    if (typeof a?.timeToFirstByte === 'number') {
      vitals.lcp_ttfb = Math.round(a.timeToFirstByte);
      if (vitals.ttfb == null) vitals.ttfb = Math.round(a.timeToFirstByte);
    }

    // LCP resource URL if available (image/video)
    vitals.lcp_url = a?.url || null;
  });

  onCLS((m: MetricWithAttribution) => {
    vitals.cls = Number(m.value.toFixed(5)); // more precision if you want
    const a: any = m.attribution;
    vitals.cls_largest_shift_target = safeSelector(a?.largestShiftTarget ?? null);
    vitals.cls_largest_shift_value  = typeof a?.largestShiftValue === 'number'
      ? Number(a.largestShiftValue.toFixed(5))
      : null;
  });

  // Prefer INP; FID fallback for very old browsers (still reported as m.value ms)
  onINP((m: MetricWithAttribution) => {
    vitals.inp = Math.round(m.value);
    const a: any = m.attribution;
    vitals.inp_interaction_target   = safeSelector(a?.eventTarget ?? null);
    vitals.inp_interaction_type     = a?.interactionType || null;
    vitals.inp_input_delay          = typeof a?.inputDelay === 'number' ? Math.round(a.inputDelay) : null;
    vitals.inp_processing_duration  = typeof a?.processingDuration === 'number' ? Math.round(a.processingDuration) : null;
    vitals.inp_presentation_delay   = typeof a?.presentationDelay === 'number' ? Math.round(a.presentationDelay) : null;
  });
  onFID?.((m: any) => { if (vitals.inp == null) vitals.inp = Math.round(m.value); });

  onFCP((m: MetricWithAttribution) => { vitals.fcp = Math.round(m.value); });
  onTTFB((m: MetricWithAttribution) => {
    vitals.ttfb = Math.round(m.value);
    const a: any = m.attribution;
    // Available in Chromium: waitingDuration, cacheDuration, dnsDuration, connectionDuration, requestDuration
    if (a) {
      if (typeof a.waitingDuration     === 'number') vitals.ttfb_wait_duration  = Math.round(a.waitingDuration);
      if (typeof a.cacheDuration       === 'number') vitals.ttfb_cache_duration  = Math.round(a.cacheDuration);
      if (typeof a.dnsDuration         === 'number') vitals.ttfb_dns_duration    = Math.round(a.dnsDuration);
      if (typeof a.connectionDuration  === 'number') vitals.ttfb_tcp_duration    = Math.round(a.connectionDuration);
      if (typeof a.requestDuration     === 'number') vitals.ttfb_req_duration    = Math.round(a.requestDuration);
    }
  });

  // Send immediately on page load
  maybeSend('first');

  // Send when navigating away from page (beforeunload equivalent)
  addEventListener('pagehide', () => { void maybeSend('pagehide'); });

  // Remove visibility change listener since we only want reload events
  // document.addEventListener('visibilitychange', () => {
  //   if (document.visibilityState === 'hidden') { void maybeSend('visibility'); }
  // });
})();
